// REACT_APP_API_URL is the bare origin (http://localhost:5000 in dev,
// https://teach-ai-db-backend.vercel.app in prod - see client/.env.*),
// never includes a /api suffix itself. Every db_backend route lives under
// /api (see apiAxiosInstance's convention of prefixing each call site with
// /api/... against a bare-origin baseURL) - append it here once so every
// function below can keep writing `${apiUrl}/learners/...` unprefixed.
// This was a live 404 on every one of this file's functions before this
// fix - confirmed via e2e/homeschool-loop.spec.ts's first real run, since
// nothing had called this file end-to-end before Phase 4's hooks existed.
const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;

export interface LearnerMastery {
  // Populated by db_backend/api/skillMastery.js's .populate('skillId', 'code name subject gradeLevel')
  skillId: {
    _id: string;
    code: string;
    name: string;
    subject: string;
    gradeLevel: number;
  };
  status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
  confidence: number;
  evidenceCount: number;
  correctCount: number;
  distinctDays: number;
  lastObservedAt?: string;
  // Phase 6: the parent's correction, if any (never overwrites `status`
  // above - see db_backend/services/masteryService.js's `effectiveStatus`).
  // Use client/src/utils/masteryStatus.ts's `effectiveStatus()` rather than
  // reading `status` directly wherever this is displayed or ranked on.
  parentOverride?: {
    status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
    setBy?: string;
    setAt?: string;
    note?: string;
  } | null;
  // Server-precomputed equivalent of effectiveStatus(this) - present on
  // every GET .../mastery[/​:skillId] response as of Phase 6.
  effectiveStatus?: 'not-attempted' | 'emerging' | 'developing' | 'secure';
}

export interface LearningEvidenceItem {
  _id: string;
  learnerId: string;
  skillId: string;
  skillCode?: string;
  source: 'assessment' | 'practice' | 'recheck' | 'observation' | 'parent_override';
  correct: boolean | null;
  response?: string;
  observedAt: string;
}

// Phase 6 (Parent progress view) - see db_backend/services/digestService.js.
export interface WeeklyDigest {
  learnerId: string;
  since: string;
  until: string;
  transitions: Array<{
    skillId: string;
    code: string | null;
    name: string;
    statusBefore: 'not-attempted' | 'emerging' | 'developing' | 'secure';
    statusAfter: 'not-attempted' | 'emerging' | 'developing' | 'secure';
  }>;
  overridesInPeriod: Array<{
    skillId: string;
    code: string | null;
    name: string | null;
    status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
    note: string | null;
    setAt: string;
  }>;
  itemsAttempted: number;
  // Distinct days with any recorded activity in the window - the honest,
  // evidence-backed substitute for "time on task", which isn't real data
  // anywhere in this codebase. See PHASE_PROMPTS.md's Phase 6 Correction 6.
  activeDays: number;
  topNextSkills: Array<{
    skillId: string;
    code: string;
    name: string;
    difficulty: string;
    reason: string;
  }>;
}

// Phase 4 (core learning loop) shapes - see db_backend/api/{placement,practice,teach}.js.
export interface PlacementSkill {
  skillId: string;
  code: string;
  name: string;
  gradeLevel: number;
}

export interface PlacementStart {
  placement: {
    skills: PlacementSkill[];
    itemsPerSkill: number;
    estimatedDurationMinutes: number;
  };
}

export interface GeneratedItemInput {
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation?: string;
  answerType: 'multiple_choice' | 'text';
  bloomsLevel?: string;
}

export interface CachedItem {
  _id: string;
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation: string;
  standardRefId: string;
  bloomsLevel: string;
  answerType: 'multiple_choice' | 'text';
}

export interface PlacementMasteryRow {
  skillId: string;
  skillCode: string;
  skillName: string;
  status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
  confidence: number;
  evidenceCount: number;
  correctCount: number;
}

export interface PracticeItemsResponse {
  items: CachedItem[];
  needsGeneration: boolean;
  skillId: string;
  skillCode: string;
  skillName: string;
  gradeLevel?: number;
  requestedCount?: number;
}

export interface PracticeSubmitResponse {
  mastery: {
    skillId: string;
    skillCode: string;
    skillName: string;
    status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
    confidence: number;
    evidenceCount: number;
    correctCount: number;
  };
}

// Phase 5 server-built context (see db_backend/services/learnerContextService.js).
// Populated only when FEATURE_SERVER_CONTEXT is on; null otherwise. Deliberately
// contains no learner name or other PII beyond grade level.
export interface LearnerAiContext {
  learnerGradeLevel: string;
  skill: {
    skillId: string;
    code: string;
    name: string;
    description: string;
    learningObjective: string;
    gradeLevel: number;
    standards: Array<{ framework: string; standard: string }>;
  };
  prerequisites: Array<{ skillId: string; code: string; name: string }>;
  mastery: {
    status: 'not-attempted' | 'emerging' | 'developing' | 'secure';
    confidence: number;
    evidenceCount: number;
    correctCount: number;
    distinctDays: number;
  };
  recentEvidence: Array<{ correct: boolean | null; source: string; observedAt: string }>;
}

export interface TeachSessionResponse {
  sessionId: string;
  // Present only when FEATURE_SERVER_CONTEXT is on server-side.
  context: LearnerAiContext | null;
  skill: {
    skillId: string;
    code: string;
    name: string;
    description: string;
    gradeLevel: number;
    learningObjective: string;
    standards: Array<{ framework: string; standard: string }>;
  };
  // Deliberately no `firstName` - the child's name is never sent to the
  // model (see IMPLEMENTATION_STATE.md Phase 5). Only fields the AI prompt
  // actually needs are returned here.
  learner: {
    gradeLevel: string;
  };
}

export const learnerService = {
  // Fetch learner's mastery across all skills
  async getMastery(learnerId: string, token: string) {
    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/mastery`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch mastery');
    return response.json();
  },

  // Fetch mastery for a specific skill
  async getSkillMastery(learnerId: string, skillId: string, token: string) {
    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/mastery/${skillId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch skill mastery');
    return response.json();
  },

  // Fetch evidence for a learner
  async getEvidence(
    learnerId: string,
    token: string,
    options?: { skillId?: string; since?: string; limit?: number }
  ) {
    const params = new URLSearchParams();
    if (options?.skillId) params.append('skillId', options.skillId);
    if (options?.since) params.append('since', options.since);
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/evidence?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch evidence');
    return response.json();
  },

  // Fetch next recommended skills
  async getNextSkills(
    learnerId: string,
    token: string,
    options?: { subject?: string; limit?: number }
  ) {
    const params = new URLSearchParams();
    if (options?.subject) params.append('subject', options.subject);
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/next?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch next skills');
    return response.json();
  },

  // Fetch skill graph
  async getSkills(
    token: string,
    options?: { subject?: string; gradeLevel?: number; graphVersion?: string }
  ) {
    const params = new URLSearchParams();
    if (options?.subject) params.append('subject', options.subject);
    if (options?.gradeLevel) params.append('gradeLevel', options.gradeLevel.toString());
    if (options?.graphVersion) params.append('graphVersion', options.graphVersion);

    const response = await fetch(
      `${apiUrl}/skills?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch skills');
    return response.json();
  },

  // Fetch skill graph as edges/nodes
  async getSkillGraph(token: string) {
    const response = await fetch(
      `${apiUrl}/skills/graph`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch skill graph');
    return response.json();
  },

  // Fetch individual skill details
  async getSkill(skillId: string, token: string) {
    const response = await fetch(
      `${apiUrl}/skills/${skillId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) throw new Error('Failed to fetch skill');
    return response.json();
  },

  // Submit a mastery override (Phase 6). Matches POST
  // /api/learners/:learnerId/mastery/:skillId/override's exact contract in
  // db_backend/api/skillMastery.js.
  async overrideMastery(
    learnerId: string,
    skillId: string,
    status: string,
    note: string,
    token: string
  ): Promise<{ mastery: LearnerMastery; evidence: LearningEvidenceItem }> {
    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/mastery/${skillId}/override`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, note })
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to override mastery');
    }
    return response.json();
  },

  // --- Phase 6: weekly digest ---

  async getDigest(
    learnerId: string,
    token: string,
    options?: { since?: string; until?: string }
  ): Promise<{ digest: WeeklyDigest }> {
    const params = new URLSearchParams();
    if (options?.since) params.append('since', options.since);
    if (options?.until) params.append('until', options.until);

    const response = await fetch(`${apiUrl}/learners/${learnerId}/digest?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to fetch weekly digest');
    return response.json();
  },

  // --- Phase 4: placement ---

  async startPlacement(learnerId: string, token: string, subject?: string): Promise<PlacementStart> {
    const query = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    const response = await fetch(`${apiUrl}/learners/${learnerId}/placement/start${query}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to start placement');
    }
    return response.json();
  },

  // Validate + cache client-generated items for one placement skill.
  async submitPlacementSkillItems(
    learnerId: string,
    skillId: string,
    generatedItems: GeneratedItemInput[],
    token: string
  ): Promise<{ items: CachedItem[]; skillId: string; skillCode: string; cachedCount: number; rejectedCount: number }> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/placement/${skillId}/items`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: generatedItems })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to validate placement items');
    }
    return response.json();
  },

  async submitPlacement(
    learnerId: string,
    answers: Record<string, string>,
    token: string,
    subject?: string
  ): Promise<{ mastery: PlacementMasteryRow[] }> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/placement/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers, subject })
    });
    if (!response.ok) throw new Error('Failed to submit placement');
    return response.json();
  },

  // --- Phase 4: practice / recheck (same endpoints; source differs server-side) ---

  async getPracticeItems(learnerId: string, skillId: string, token: string, count = 3): Promise<PracticeItemsResponse> {
    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/practice/${skillId}/items?count=${count}`,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    if (!response.ok) throw new Error('Failed to fetch practice items');
    return response.json();
  },

  async submitPracticeSkillItems(
    learnerId: string,
    skillId: string,
    generatedItems: GeneratedItemInput[],
    token: string
  ): Promise<{ items: CachedItem[]; skillId: string; skillCode: string; skillName: string; cachedCount: number; rejectedCount: number }> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/practice/${skillId}/items`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: generatedItems })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to validate practice items');
    }
    return response.json();
  },

  async submitPractice(
    learnerId: string,
    skillId: string,
    answers: Record<string, string>,
    token: string
  ): Promise<PracticeSubmitResponse> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/practice/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ skillId, answers })
    });
    if (!response.ok) throw new Error('Failed to submit practice');
    return response.json();
  },

  // --- Phase 4: recheck (mirrors practice; source differs server-side) ---

  async getRecheckItems(learnerId: string, skillId: string, token: string, count = 3): Promise<PracticeItemsResponse> {
    const response = await fetch(
      `${apiUrl}/learners/${learnerId}/recheck/${skillId}/items?count=${count}`,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    if (!response.ok) throw new Error('Failed to fetch recheck items');
    return response.json();
  },

  async submitRecheckSkillItems(
    learnerId: string,
    skillId: string,
    generatedItems: GeneratedItemInput[],
    token: string
  ): Promise<{ items: CachedItem[]; skillId: string; skillCode: string; skillName: string; cachedCount: number; rejectedCount: number }> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/recheck/${skillId}/items`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: generatedItems })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to validate recheck items');
    }
    return response.json();
  },

  async submitRecheck(
    learnerId: string,
    skillId: string,
    answers: Record<string, string>,
    token: string
  ): Promise<PracticeSubmitResponse> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/recheck/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ skillId, answers })
    });
    if (!response.ok) throw new Error('Failed to submit recheck');
    return response.json();
  },

  // --- Phase 4: teach ---

  async startTeachSession(learnerId: string, skillId: string, token: string): Promise<TeachSessionResponse> {
    const response = await fetch(`${apiUrl}/learners/${learnerId}/teach/${skillId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to start teach session');
    return response.json();
  }
};
